import { z } from 'zod';

import type { Client, Invoice, Organization } from './dtos/index';

/**
 * Catalogue of all renderable invoice templates.
 *
 * This module is the **single source of truth** for the template
 * line-up. Both consumers import from here:
 *
 *   - `apps/artboard/src/templates/index.ts` — registers React
 *     components keyed off `id`, falling back to `'classic'`.
 *   - `apps/mobile/app/invoice/[id].tsx` — renders the Design tab
 *     of the invoice workspace using the `name`, `description`,
 *     `accent`, and `thumbnail`.
 *
 * To add a new template a developer:
 *   1. Adds an entry to `TEMPLATE_MANIFEST` below.
 *   2. Implements the React component in `apps/artboard/src/templates/<id>/`.
 *   3. Registers the component in `apps/artboard/src/templates/index.ts`.
 *   4. (Optional) Drops a thumbnail SVG inline into the manifest entry.
 *
 * Nothing else has to change — the server's printer pipeline already
 * rounds-trips `templateId` through the cache key, and the mobile
 * picker pulls names + thumbnails from this manifest.
 */
export const TemplateIdSchema = z.enum([
  'classic',
  'modern',
  'minimal',
  'executive',
  'studio',
  'compact',
]);
export type TemplateId = z.infer<typeof TemplateIdSchema>;

export interface TemplateManifestEntry {
  /** Stable id used as the cache key and DB column value. */
  id: TemplateId;
  /** Human-readable name surfaced in the picker UI. */
  name: string;
  /** One-sentence pitch — what kind of business this template flatters. */
  description: string;
  /**
   * Suggested accent colour in the picker preview. Real renders use
   * `organization.branding.accent` and fall back to this only when no
   * brand colour has been set.
   */
  accent: string;
  /**
   * Inline SVG sized 120 × 170 (an A4 silhouette). Rendered behind a
   * gradient backdrop so colours pop in the picker. Intentionally
   * lightweight — the heavy fidelity preview comes from the printer
   * pipeline once the user commits.
   */
  thumbnail: string;
}

type ThumbnailVariant =
  | 'block'
  | 'thin'
  | 'minimal'
  | 'executive'
  | 'studio'
  | 'compact';

const headerSwatch = (accent: string, kind: ThumbnailVariant): string => {
  if (kind === 'block') {
    return `<rect x="0" y="0" width="120" height="32" fill="${accent}" />` +
      `<rect x="10" y="10" width="40" height="4" fill="white" opacity="0.85" rx="1" />` +
      `<rect x="10" y="18" width="22" height="3" fill="white" opacity="0.6" rx="1" />`;
  }
  if (kind === 'thin') {
    return `<rect x="10" y="10" width="100" height="2" fill="${accent}" rx="1" />`;
  }
  if (kind === 'minimal') {
    return `<rect x="10" y="14" width="32" height="3" fill="${accent}" rx="1" />`;
  }
  if (kind === 'executive') {
    // Centered "INVOICE" wordmark sandwiched between two thin rules,
    // a small letterhead block top-left, and contact lines top-right.
    return (
      // Letterhead block (logo + brand)
      `<rect x="10" y="10" width="6" height="6" rx="1" fill="${accent}" />` +
      `<rect x="20" y="11" width="22" height="2" fill="#1B2236" opacity="0.85" rx="1" />` +
      `<rect x="20" y="15" width="14" height="2" fill="#1B2236" opacity="0.4" rx="1" />` +
      // Contact lines
      `<rect x="74" y="11" width="36" height="2" fill="#1B2236" opacity="0.4" rx="1" />` +
      `<rect x="84" y="15" width="26" height="2" fill="#1B2236" opacity="0.4" rx="1" />` +
      // Wordmark with hairline rules
      `<rect x="10" y="32" width="34" height="1" fill="${accent}" opacity="0.45" />` +
      `<rect x="76" y="32" width="34" height="1" fill="${accent}" opacity="0.45" />` +
      `<rect x="48" y="29" width="24" height="6" fill="${accent}" rx="1" />`
    );
  }
  if (kind === 'studio') {
    // Vertical accent rail down the left edge.
    return (
      `<rect x="0" y="0" width="22" height="170" fill="${accent}" />` +
      `<rect x="6" y="14" width="2" height="42" fill="white" opacity="0.85" rx="1" />` +
      `<rect x="6" y="120" width="2" height="36" fill="white" opacity="0.6" rx="1" />` +
      `<rect x="32" y="14" width="40" height="3" fill="#1B2236" rx="1" />` +
      `<rect x="32" y="20" width="22" height="2" fill="#1B2236" opacity="0.4" rx="1" />` +
      `<rect x="78" y="14" width="32" height="2" fill="#1B2236" opacity="0.4" rx="1" />` +
      `<rect x="78" y="18" width="32" height="2" fill="#1B2236" opacity="0.4" rx="1" />`
    );
  }
  // 'compact'
  // Tight metadata strip with 4 cells right under the org name.
  return (
    `<rect x="10" y="10" width="32" height="3" fill="#1B2236" rx="1" />` +
    `<rect x="80" y="11" width="30" height="4" fill="${accent}" opacity="0.85" rx="1" />` +
    `<rect x="10" y="22" width="100" height="10" fill="#1B2236" opacity="0.06" rx="2" />` +
    `<rect x="14" y="25" width="18" height="2" fill="#1B2236" opacity="0.45" rx="1" />` +
    `<rect x="38" y="25" width="18" height="2" fill="#1B2236" opacity="0.45" rx="1" />` +
    `<rect x="62" y="25" width="18" height="2" fill="#1B2236" opacity="0.45" rx="1" />` +
    `<rect x="86" y="25" width="20" height="2" fill="${accent}" rx="1" />`
  );
};

const bodyLines = (startY: number, leftPad = 10): string => {
  let svg = '';
  for (let i = 0; i < 6; i += 1) {
    const y = startY + i * 12;
    const w = i % 2 === 0 ? 95 - (leftPad - 10) : 75 - (leftPad - 10);
    svg +=
      `<rect x="${leftPad}" y="${y}" width="${w}" height="2" fill="#3D466A" opacity="0.18" rx="1" />`;
  }
  return svg;
};

const totalsCluster = (startY: number, accent: string, variant?: ThumbnailVariant): string => {
  if (variant === 'studio') {
    // Oversize grand-total number anchored bottom-right, no row stack.
    return (
      `<rect x="74" y="${startY + 8}" width="36" height="6" fill="${accent}" rx="1" />`
    );
  }
  if (variant === 'compact') {
    // Hero row bar across the bottom in accent, with 2 quiet rows above.
    return (
      `<rect x="68" y="${startY}" width="42" height="3" fill="#3D466A" opacity="0.25" rx="1" />` +
      `<rect x="68" y="${startY + 6}" width="42" height="3" fill="#3D466A" opacity="0.25" rx="1" />` +
      `<rect x="68" y="${startY + 12}" width="42" height="6" fill="${accent}" rx="1" />`
    );
  }
  return (
    `<rect x="68" y="${startY}" width="42" height="3" fill="#3D466A" opacity="0.25" rx="1" />` +
    `<rect x="68" y="${startY + 6}" width="42" height="3" fill="#3D466A" opacity="0.25" rx="1" />` +
    `<rect x="68" y="${startY + 14}" width="42" height="4" fill="${accent}" rx="1" />`
  );
};

const buildThumbnail = (variant: ThumbnailVariant, accent: string): string => {
  const leftPad = variant === 'studio' ? 32 : 10;
  const bodyStart = variant === 'block' ? 50 : variant === 'executive' ? 50 : 40;
  const totalsStart = variant === 'block' ? 138 : variant === 'executive' ? 140 : 130;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 170" width="120" height="170">` +
    `<rect x="0" y="0" width="120" height="170" fill="white" />` +
    headerSwatch(accent, variant) +
    bodyLines(bodyStart, leftPad) +
    totalsCluster(totalsStart, accent, variant) +
    `</svg>`
  );
};

export const TEMPLATE_MANIFEST: readonly TemplateManifestEntry[] = [
  {
    id: 'classic',
    name: 'Classic',
    description:
      'Refined, balanced ledger layout. The safe choice for any industry.',
    accent: '#3D466A',
    thumbnail: buildThumbnail('thin', '#3D466A'),
  },
  {
    id: 'modern',
    name: 'Modern',
    description:
      'Bold accent header with confident typography. Great for studios and agencies.',
    accent: '#1F6FEB',
    thumbnail: buildThumbnail('block', '#1F6FEB'),
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description:
      'Quiet, monochrome, premium. Lets the numbers do the talking.',
    accent: '#0F1320',
    thumbnail: buildThumbnail('minimal', '#0F1320'),
  },
  {
    id: 'executive',
    name: 'Executive',
    description:
      'Serif wordmark, ledger ruling, navy and ivory. Reads like a notarised statement — built for law, finance, and senior consulting.',
    accent: '#14213D',
    thumbnail: buildThumbnail('executive', '#14213D'),
  },
  {
    id: 'studio',
    name: 'Studio',
    description:
      'Bleed-edge accent rail and an oversized grand total. The invoice itself becomes part of the brand experience.',
    accent: '#E0533C',
    thumbnail: buildThumbnail('studio', '#E0533C'),
  },
  {
    id: 'compact',
    name: 'Compact',
    description:
      'Zebra-striped table, tight metadata strip, numbered rows. The right pick for retainers, distributors, and any 30-line invoice.',
    accent: '#0E7C4A',
    thumbnail: buildThumbnail('compact', '#0E7C4A'),
  },
];

const MANIFEST_BY_ID = new Map(TEMPLATE_MANIFEST.map((t) => [t.id, t]));

export function getTemplate(id: string | null | undefined): TemplateManifestEntry {
  if (id && MANIFEST_BY_ID.has(id as TemplateId)) {
    return MANIFEST_BY_ID.get(id as TemplateId)!;
  }
  return MANIFEST_BY_ID.get('classic')!;
}

export const DEFAULT_TEMPLATE_ID: TemplateId = 'classic';

/**
 * The complete payload the artboard SPA expects to render an invoice.
 *
 * Two transports populate it today:
 *   - **Server-side (puppeteer)** — `PrinterService` calls
 *     `page.evaluateOnNewDocument` to seed
 *     `window.localStorage["artboard:invoice"]` *before* React mounts
 *     so the first paint already has data. The artboard's
 *     `useArtboardPayload` hook reads it synchronously via
 *     `useSyncExternalStore`.
 *   - **Client-side (mobile WebView)** — the host loads the artboard
 *     and pushes payload updates over `postMessage` using
 *     {@link ArtboardInbound} (`{ type: 'SET_INVOICE', payload }`).
 *     The artboard's `messageBridge` calls `setPayload(payload)`
 *     which notifies the same store and triggers a re-render.
 *
 * Lives here (and not inside `apps/artboard`) so the mobile app can
 * import the exact same shape it sends across the wire — no app
 * boundary in the way, no chance of drift between sender and
 * receiver.
 */
export interface ArtboardPayload {
  invoice: Invoice;
  organization: Organization;
  client: Client | null;
  /** Renderer template id, e.g. `'classic'`. Defaults to `classic`. */
  templateId?: string;
}

/**
 * Messages the artboard accepts on `window.addEventListener('message')`.
 *
 * `id` is an optional opaque correlation token — the host can include
 * one to match the eventual `INVOICE_RECEIVED` ack and detect lost
 * messages without re-sending the full payload.
 */
export type ArtboardInbound =
  | { type: 'SET_INVOICE'; id?: string; payload: ArtboardPayload }
  | { type: 'PING'; id?: string };

/**
 * Messages the artboard posts back to its host. The mobile WebView
 * uses these to clear handshake timers (`INVOICE_RECEIVED`), gate
 * "ready to show" state (`RENDER_READY`), and detect liveness during
 * silent loads (`PONG`).
 */
export type ArtboardOutbound =
  | { type: 'INVOICE_RECEIVED'; id?: string }
  | { type: 'PONG'; id?: string }
  | { type: 'RENDER_READY'; pageCount: number };

/**
 * Storage key the artboard reads on boot. Hosts that want to seed
 * the very first paint (puppeteer, the WebView's
 * `injectedJavaScriptBeforeContentLoaded`) write the payload here.
 * Exported so both transports stay in lockstep.
 */
export const ARTBOARD_PAYLOAD_STORAGE_KEY = 'artboard:invoice';
